import { createFileRoute, redirect, useSearch } from '@tanstack/react-router'
import { useCallback, useEffect, useState } from 'react'

import { useVaultContext } from './-vault-context'
import { useClientProject } from '@/contexts'
import { normalizeEntityId, toShortEntityId } from '@/lib/entityUrl'
import { ClientsView } from './-vault/ClientsView'
import { ProjectsView } from './-vault/ProjectsView'
import { ContractsView } from './-vault/ContractsView'
import { authService } from '@/services/authService'
import { getClients, getProjects, listAccessibleProjects } from '@/services/clientService'
import { getSharedWithMe, type SharedContract } from '@/services/contractService'
import { getClientAccessLevel } from '@/services/userManagementService'
import type { Project } from '@/types'
import { isGlobalAdmin } from '@/types'

export const Route = createFileRoute('/_app/law/vault/')({
  head: () => ({ meta: [{ title: 'Vault · AERRIS' }] }),
  // Scope a non-global-admin to where their access actually is, before the page renders (so no
  // content they can't reach ever flashes, and the scope can't be escaped via browser navigation):
  //  - exactly one accessible client → that client's project page;
  //  - within it, if they are NOT a client admin and the client holds a single project, straight to
  //    that project's contract list — client access cascades to every project, so a lone project is
  //    the only one they can reach, and they are locked to it.
  // Global admins, and users with zero or many clients, are left to browse the full client list.
  beforeLoad: async ({ search }) => {
    const session = await authService.ensureSession()
    // Not signed in / server unreachable: let the app shell (_app) decide what to do.
    if (session.status !== 'authenticated') return
    if (isGlobalAdmin(session.user.role)) return

    const clients = await getClients()
    if (clients.length !== 1) return
    const theClient = clients[0]

    const [level, projects] = await Promise.all([
      getClientAccessLevel(theClient.uuid).catch(() => null),
      getProjects(theClient.uuid).catch(() => [] as Awaited<ReturnType<typeof getProjects>>),
    ])

    // The URL carries the minimal identity (a project implies its client), in the canonical
    // short form; incoming params are normalized so legacy dashed-UUID links compare equal.
    if (level !== 'ADMIN' && projects.length === 1) {
      const onlyProjectId = projects[0].uuid
      const selectedProjectId = normalizeEntityId(search.projectId)
      if (selectedProjectId !== onlyProjectId) {
        // A foreign project they hold a direct grant on (ADR 0025 S7) is reachable from
        // "Shared with me" — being locked to their lone project must not bounce them off it.
        if (selectedProjectId) {
          const accessible = await listAccessibleProjects().catch(() => [] as Project[])
          if (accessible.some((p) => p.uuid === selectedProjectId)) return
        }
        throw redirect({
          to: '/law/vault',
          search: (prev) => ({ ...prev, clientId: undefined, projectId: toShortEntityId(onlyProjectId) }),
          replace: true,
        })
      }
      return
    }

    // A project of their client is a sufficient (and minimal) identity on its own; anything
    // else — a foreign/unknown project, or a client other than theirs — pins them back to
    // their client, clearing the project (as before). A foreign project they hold a DIRECT
    // grant on is the exception (ADR 0025 S7): it is a legitimate destination reached from
    // "Shared with me", so it survives the scoping.
    const selectedProjectId = normalizeEntityId(search.projectId)
    let projectIsTheirs = Boolean(selectedProjectId && projects.some((p) => p.uuid === selectedProjectId))
    if (!projectIsTheirs && selectedProjectId) {
      const accessible = await listAccessibleProjects().catch(() => [] as Project[])
      projectIsTheirs = accessible.some((p) => p.uuid === selectedProjectId)
    }
    if (!projectIsTheirs && normalizeEntityId(search.clientId) !== theClient.uuid) {
      throw redirect({
        to: '/law/vault',
        search: (prev) => ({ ...prev, clientId: toShortEntityId(theClient.uuid), projectId: undefined }),
        replace: true,
      })
    }
  },
  component: VaultIndexPage,
})

function VaultIndexPage() {
  const {
    projectId,
    filteredContracts,
    loadingContracts,
    loadingProjects,
    clientId,
    clients,
    allProjects,
    selectedClient,
  } = useVaultContext()

  const { setClientId, setProjectId } = useClientProject()
  // Deep-link folder target from the URL (set by the Data Center's Recent Uploads links).
  // Either id form is accepted; consumers below need the UUID.
  const { folderId: rawDeepLinkFolderId } = useSearch({ from: '/_app/law/vault' })
  const deepLinkFolderId = normalizeEntityId(rawDeepLinkFolderId)

  // The caller's "Shared with me" surface (ADR 0025 S7): their direct/folder-granted contracts,
  // and any accessible project no accessible client accounts for (a direct project grant — the
  // per-client loading above never finds those). Both are self-scoped reads; a failure just leaves
  // the section empty, exactly like holding no grants.
  const [sharedContracts, setSharedContracts] = useState<SharedContract[]>([])
  const [accessibleProjects, setAccessibleProjects] = useState<(Project & { clientUuid: string })[]>([])
  useEffect(() => {
    let cancelled = false
    getSharedWithMe()
      .then((shared) => {
        if (!cancelled) setSharedContracts(shared)
      })
      .catch(() => undefined)
    listAccessibleProjects()
      .then((accessible) => {
        if (!cancelled) setAccessibleProjects(accessible)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [])
  const grantedProjects = accessibleProjects.filter(
    (project) => !clients.some((client) => client.uuid === project.clientUuid),
  )

  const handleSelectClient = useCallback((uuid: string) => {
    setClientId(uuid)
  }, [setClientId])

  const handleSelectProject = useCallback((uuid: string) => {
    setProjectId(uuid)
  }, [setProjectId])

  const handleBackToClients = useCallback(() => {
    setClientId(undefined)
  }, [setClientId])

  // No client selected — show client folders, plus whatever is shared with the caller directly
  // (for a zero-client user, the shared section is the whole vault).
  if (!clientId && !projectId) {
    return (
      <ClientsView
        clients={clients}
        loadingContracts={loadingContracts}
        onSelectClient={handleSelectClient}
        sharedContracts={sharedContracts}
        grantedProjects={grantedProjects}
        onSelectProject={handleSelectProject}
      />
    )
  }

  // Client selected, no project — show project folders for the selected client
  if (clientId && !projectId) {
    return (
      <ProjectsView
        clientId={clientId}
        selectedClient={selectedClient}
        allProjects={allProjects}
        filteredContracts={filteredContracts}
        loadingProjects={loadingProjects}
        onBack={handleBackToClients}
        onSelectProject={handleSelectProject}
      />
    )
  }

  // Project selected — flat list of contracts in table view
  return (
    <ContractsView
      contracts={filteredContracts}
      loadingContracts={loadingContracts}
      clientId={clientId || ''}
      projectId={projectId || ''}
      clients={clients}
      allProjects={allProjects}
      initialFolderId={deepLinkFolderId}
    />
  )
}
